/**
 * Las reglas visuales de un nodo de texto suelto: aire, tamaño y color.
 *
 * Es el gemelo de `shapeStyle.ts` para el otro texto del lienzo, y existe por
 * el mismo motivo que aquél: son funciones puras, sin estado y sin acceso al
 * store, así que la vista (`CardView`), la capa de texto y su editor sacan sus
 * medidas exactamente del mismo lugar. Que las tres salgan de acá es lo que
 * evita que el texto se mueva al entrar o salir de edición.
 *
 * La diferencia con una forma es la que se ve: el fondo puede no existir, y la
 * alineación y el color de las letras los elige quien escribe en vez de salir
 * de acá — el color de una forma se calcula por contraste contra su relleno; el
 * de un texto se guarda en el nodo (ver `TextData`).
 */

import { type TextData, TEXT_BG_NONE } from "../model/types.ts";
import {
  type Box,
  CANVAS_TEXT_LINE_PX,
  measureTextBlock,
  measureTextHeight,
} from "./canvasText.ts";

/**
 * Aire entre el texto y el borde del nodo, en px de mundo.
 *
 * Es menos que el de una forma (12): ahí el texto tiene que despegarse de un
 * relleno de color, y acá lo normal es que no haya fondo ninguno, así que un
 * margen grande sólo agranda el recuadro de selección alrededor de la nada. Lo
 * suficiente para que, con fondo blanco, las letras no toquen el borde.
 */
export const TEXT_PAD = 8;

/** Radio del fondo blanco. El mismo de las tarjetas y los contenedores. */
export const TEXT_RADIUS = 4;

/** Hasta acá crece solo un texto auto-ancho; de ahí en más, envuelve. */
export const TEXT_MAX_W = 600;
/** Ningún nodo de texto baja de esto, ni escribiéndolo ni redimensionándolo. */
export const TEXT_MIN_W = 40;
export const TEXT_MIN_H = CANVAS_TEXT_LINE_PX + TEXT_PAD * 2;

/** La caja donde va el texto, en coordenadas locales del nodo. */
export function textNodeBox(width: number, height: number): Box {
  return {
    x: TEXT_PAD,
    y: TEXT_PAD,
    width: Math.max(0, width - TEXT_PAD * 2),
    height: Math.max(0, height - TEXT_PAD * 2),
  };
}

/**
 * El tamaño que le corresponde a un nodo de texto, dado el ancho que tiene hoy.
 *
 * Son las dos reglas de Figma, y la que se aplique depende de `autoWidth`:
 *
 *  - auto-ancho (nació de un click y nadie lo estiró): la caja es el texto, en
 *    los dos ejes, hasta `TEXT_MAX_W`.
 *  - ancho fijo (alguien lo redimensionó): el ancho es suyo y no se toca; el
 *    alto lo pide el texto envuelto a ese ancho.
 *
 * En los dos casos el alto SIGUE al texto, y ahí está la diferencia con una
 * forma —que sólo crece—: un nodo de texto no tiene nada más adentro, así que
 * dejarle alto de sobra al borrar una línea sería dejar un hueco invisible que
 * igual tapa clicks.
 */
export function textNodeSize(
  data: TextData,
  width: number,
): { width: number; height: number } {
  if (data.autoWidth) {
    const m = measureTextBlock(data.text, TEXT_MAX_W - TEXT_PAD * 2);
    return {
      width: Math.max(TEXT_MIN_W, m.width + TEXT_PAD * 2),
      height: Math.max(TEXT_MIN_H, m.height + TEXT_PAD * 2),
    };
  }
  const w = Math.max(TEXT_MIN_W, width);
  const h = measureTextHeight(data.text, w - TEXT_PAD * 2) + TEXT_PAD * 2;
  return { width: w, height: Math.max(TEXT_MIN_H, h) };
}

/** ¿Este nodo pinta algo detrás del texto? */
export function hasTextBackground(data: TextData | undefined): boolean {
  return !!data && data.background !== TEXT_BG_NONE;
}
